import { cn } from '@/lib/utils'
import type { ConfigData } from '../types'

interface Props {
  config: ConfigData | null
}

function getConfigValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((current, key) => {
    if (current && typeof current === 'object' && !Array.isArray(current)) {
      return (current as Record<string, unknown>)[key]
    }
    return undefined
  }, obj)
}

function stripProvider(model: string): string {
  const parts = model.split('/')
  return parts.length > 1 ? parts.slice(1).join('/') : model
}

const KNOWN_CHANNELS: Record<string, string> = {
  whatsappCloud: 'WhatsApp Cloud',
  whatsapp: 'WhatsApp',
  discord: 'Discord',
  telegram: 'Telegram',
  slack: 'Slack',
  signal: 'Signal',
  irc: 'IRC',
  googleChat: 'Google Chat',
  imessage: 'iMessage',
  // Kept for legacy openclaw.json configs that still declare an msteams channel block, even though the Teams UI has been removed.
  msteams: 'MS Teams',
}

const NON_CHANNEL_KEYS = new Set(['defaults', 'modelByChannel'])

function SectionHeader({ children }: { children: React.ReactNode }) {
  return <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2 mb-2">{children}</h3>
}

function KeyValue({ label, value }: { label: string; value: string | number | undefined | null }) {
  if (value === undefined || value === null) return null
  return (
    <div className="flex items-center justify-between gap-2 py-0.5 px-2">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-xs font-mono text-foreground truncate max-w-[60%] text-right">{String(value)}</span>
    </div>
  )
}

function StatusDot({ enabled, label }: { enabled: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2 py-0.5 px-2">
      <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', enabled ? 'bg-green-500' : 'bg-muted-foreground/30')} />
      <span className="text-xs text-foreground">{label}</span>
    </div>
  )
}

export default function ConfigSidebarContent({ config }: Props) {
  if (!config || (!config.config && !config.error)) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-xs text-muted-foreground">Carregando configuração...</p>
      </div>
    )
  }

  if (config.error) {
    return (
      <div className="flex-1 flex items-center justify-center px-4">
        <p className="text-xs text-muted-foreground text-center">{config.error}</p>
      </div>
    )
  }

  const cfg = config.config!

  // Model
  const modelField = getConfigValue(cfg, 'agents.defaults.model')
  const primaryModel = typeof modelField === 'string'
    ? modelField
    : typeof modelField === 'object' && modelField
      ? (modelField as Record<string, unknown>).primary as string | undefined
      : undefined
  const fallbacks = typeof modelField === 'object' && modelField && Array.isArray((modelField as Record<string, unknown>).fallbacks)
    ? ((modelField as Record<string, unknown>).fallbacks as unknown[]).filter((f): f is string => typeof f === 'string')
    : []

  // Channels
  const channelsObj = getConfigValue(cfg, 'channels') as Record<string, unknown> | undefined
  const channelEntries = channelsObj
    ? Object.entries(channelsObj)
        .filter(([key]) => !NON_CHANNEL_KEYS.has(key))
        .map(([key, value]) => ({
          key,
          label: KNOWN_CHANNELS[key] || key,
          enabled: value && typeof value === 'object' ? (value as Record<string, unknown>).enabled !== false : true,
        }))
    : []

  // Metadata
  const version = getConfigValue(cfg, 'meta.lastTouchedVersion') as string | undefined
  const lastTouchedAt = getConfigValue(cfg, 'meta.lastTouchedAt') as string | undefined
  const formattedDate = lastTouchedAt
    ? new Date(lastTouchedAt).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
    : undefined

  // Gateway
  const gatewayPort = getConfigValue(cfg, 'gateway.port') as number | undefined
  const gatewayAuth = getConfigValue(cfg, 'gateway.auth.mode') as string | undefined
  const gatewayBind = getConfigValue(cfg, 'gateway.bind') as string | undefined
  const hasGateway = gatewayPort !== undefined || gatewayAuth !== undefined || gatewayBind !== undefined

  // Hooks
  const hooksInternalEnabled = getConfigValue(cfg, 'hooks.internal.enabled')
  const hooksEntries = getConfigValue(cfg, 'hooks.internal.entries') as Record<string, unknown> | undefined
  const hooksList = hooksEntries
    ? Object.entries(hooksEntries).map(([name, value]) => ({
        name,
        enabled: hooksInternalEnabled !== false && (value && typeof value === 'object' ? (value as Record<string, unknown>).enabled !== false : true),
      }))
    : []

  // Browser
  const browserHeadless = getConfigValue(cfg, 'browser.headless') as boolean | undefined
  const browserNoSandbox = getConfigValue(cfg, 'browser.noSandbox') as boolean | undefined
  const hasBrowser = browserHeadless !== undefined || browserNoSandbox !== undefined

  return (
    <div className="flex-1 overflow-y-auto p-3 space-y-4">
      {/* Model */}
      {primaryModel && (
        <div>
          <SectionHeader>Modelo</SectionHeader>
          <div className="space-y-0.5">
            <KeyValue label="Primário" value={stripProvider(primaryModel)} />
            {fallbacks.length === 0 && <KeyValue label="Fallback" value="—" />}
            {fallbacks.map((fb, index) => (
              <KeyValue key={fb} label={fallbacks.length > 1 ? `Fallback ${index + 1}` : 'Fallback'} value={stripProvider(fb)} />
            ))}
          </div>
        </div>
      )}

      {/* Channels */}
      {channelEntries.length > 0 && (
        <div>
          <SectionHeader>Canais</SectionHeader>
          <div className="space-y-0.5">
            {channelEntries.map((ch) => (
              <StatusDot key={ch.key} enabled={ch.enabled} label={ch.label} />
            ))}
          </div>
        </div>
      )}

      {/* Instance Metadata */}
      {(version || formattedDate) && (
        <div>
          <SectionHeader>Instância</SectionHeader>
          <div className="space-y-0.5">
            <KeyValue label="Versão" value={version} />
            <KeyValue label="Atualizado em" value={formattedDate} />
          </div>
        </div>
      )}

      {/* Gateway */}
      {hasGateway && (
        <div>
          <SectionHeader>Gateway</SectionHeader>
          <div className="space-y-0.5">
            <KeyValue label="Porta" value={gatewayPort} />
            <KeyValue label="Auth" value={gatewayAuth} />
            <KeyValue label="Bind" value={gatewayBind} />
          </div>
        </div>
      )}

      {/* Internal Hooks */}
      {hooksList.length > 0 && (
        <div>
          <SectionHeader>Hooks Internos</SectionHeader>
          <div className="space-y-0.5">
            {hooksList.map((hook) => (
              <StatusDot key={hook.name} enabled={hook.enabled} label={hook.name} />
            ))}
          </div>
        </div>
      )}

      {/* Browser */}
      {hasBrowser && (
        <div>
          <SectionHeader>Browser</SectionHeader>
          <div className="space-y-0.5">
            {browserHeadless !== undefined && <StatusDot enabled={browserHeadless} label="Headless" />}
            {browserNoSandbox !== undefined && <StatusDot enabled={browserNoSandbox} label="No Sandbox" />}
          </div>
        </div>
      )}
    </div>
  )
}
